import { FunctionComponent, ReactNode } from "react";

import { Link } from "react-router-dom";

import { faList, faBolt } from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components";
import { classNames } from "@/utils/classNames";

export interface LeadCardCompactProps {
	/** Visually marks the card as the currently opened lead. */
	isActive: boolean;
	onClick: () => void;
	/** Dims the card (e.g. paused sequences). */
	dimmed?: boolean;
	/** Color labels strip rendered above the card body (LeadColorLabels). */
	topLabels?: ReactNode;
	/** Optional bulk-selection checkbox. */
	checkbox?: ReactNode;
	/** Vertical gutter: score badge, channel icon, sender avatar, color dots. */
	gutter?: ReactNode;
	/** First row: the lead's name block (may embed links/review affordances). */
	name: ReactNode;
	/** Right-aligned time/date caption of the first row. */
	time?: string | null;
	/** Second row: the lead's job title block. */
	title?: ReactNode;
	/** Third row: company name (+ category) block. */
	company?: ReactNode;
	/** Where the lead came from: its list and campaign (or chat outreach). */
	provenance?: {
		listName: string;
		/** The campaign (or chat outreach) half. Omitted for a lead nobody has contacted yet. */
		campaignLabel?: string | null;
		/** List-detail route the list name links to. Plain text when omitted. */
		listTo?: string;
	} | null;
	/** Status chip row (sentiment dropdown on the Inbox, status chip on the Outbox). */
	chips?: ReactNode;
	/** Two-line message preview. */
	preview?: ReactNode;
	/** Step-progress strip (Outbox only). */
	steps?: ReactNode;
	/** Extra rows below the preview (CRM syncs, research chips …). */
	footer?: ReactNode;
}

/**
 * The list half of the provenance row. With a route it is a link back to the list detail
 * (PREC-4028, restoring what the pre-compact card offered): opened in a new tab so the Messages
 * column keeps its thread and scroll position, and with the click stopped so following it does
 * not also re-select the lead — the whole card body carries an `onClick`.
 */
const ProvenanceList: FunctionComponent<{ listName: string; listTo?: string }> = ({
	listName,
	listTo,
}) => {
	const content = (
		<>
			<Icon className="w-2.5 h-2.5 shrink-0" icon={faList} />
			<span className="truncate">{listName}</span>
		</>
	);

	if (!listTo) {
		return <span className="flex items-center gap-1.5 min-w-0">{content}</span>;
	}

	return (
		<Link
			className="flex items-center gap-1.5 min-w-0 hover:underline hover:text-status-info"
			rel="noreferrer"
			target="_blank"
			to={listTo}
			onClick={(event) => event.stopPropagation()}
		>
			{content}
		</Link>
	);
};

/**
 * Presentational shell of the compact Messages lead card (PREC-3850 mockup): a
 * gutter column next to a single-column main block — name+time, title, company,
 * provenance line, chips, preview, and the step progress strip.
 */
export const LeadCardCompact: FunctionComponent<LeadCardCompactProps> = ({
	isActive,
	onClick,
	dimmed = false,
	topLabels,
	checkbox,
	gutter,
	name,
	time,
	title,
	company,
	provenance,
	chips,
	preview,
	steps,
	footer,
}) => (
	<div className={classNames("lead-item flex flex-row gap-2", dimmed ? "opacity-70" : "")}>
		<div
			className={classNames(
				"w-full flex flex-col gap-1.5 border-b border-t border-t-transparent border-x border-x-transparent cursor-pointer relative overflow-hidden p-2.5",
				isActive
					? "border !border-blue-action border-t-blue-action border-x-blue-action bg-brand-white"
					: "border-border-light bg-white hover:border hover:bg-brand-white/50 hover:border-blue-action/50"
			)}
			onClick={onClick}
		>
			{topLabels}
			<div className="flex flex-row gap-2.5 w-full">
				{checkbox && <div className="flex justify-center items-start shrink-0">{checkbox}</div>}
				{gutter && (
					<div className="flex flex-col gap-1.5 items-center shrink-0 w-6 pt-0.5">{gutter}</div>
				)}
				<div className="flex flex-col gap-0.5 grow min-w-0">
					<div className="flex flex-row items-center gap-2 w-full min-w-0">
						<div className="grow min-w-0 font-bold text-sm">{name}</div>
						{time && (
							<span className="text-xs text-gray-500 font-medium shrink-0 whitespace-nowrap">
								{time}
							</span>
						)}
					</div>
					{title && <div className="text-xs text-text-meta min-w-0">{title}</div>}
					{company && (
						<div className="flex flex-row items-center gap-2 w-full min-w-0">
							<div className="grow min-w-0 text-xs font-semibold">{company}</div>
							{/* Invisible twin of the name row's time caption: reserves the same right
							    inset so the company-row icon cluster aligns vertically under the
							    name-row cluster. */}
							{time && (
								<span
									aria-hidden
									className="invisible text-xs font-medium shrink-0 whitespace-nowrap"
								>
									{time}
								</span>
							)}
						</div>
					)}
					{provenance && (
						<div className="flex items-center gap-1.5 text-xs text-gray-500 truncate whitespace-nowrap">
							<ProvenanceList listName={provenance.listName} listTo={provenance.listTo} />
							{provenance.campaignLabel && (
								<>
									<span className="w-[3px] h-[3px] rounded-full bg-gray-300 shrink-0" />
									<Icon className="w-2.5 h-2.5 shrink-0" icon={faBolt} />
									<span className="truncate">{provenance.campaignLabel}</span>
								</>
							)}
						</div>
					)}
					{chips && <div className="flex items-center gap-1.5 flex-wrap mt-1">{chips}</div>}
					{preview && (
						<div className="text-xs text-text-meta leading-normal line-clamp-2 mt-0.5">
							{preview}
						</div>
					)}
					{steps}
					{footer}
				</div>
			</div>
		</div>
	</div>
);
